import React, { useState, useEffect, useCallback } from 'react';
import settlementService from '../../services/settlementService';

export const FinancialSummaryTab = ({
  user,
  centres = [],
  showToast,
  onOpenReceipt,
  onNavigateToTab
}) => {
  const isAccountsOrAdmin =
    user?.roleName?.toUpperCase() === 'ADMINISTRATOR' ||
    user?.roleId === 1 ||
    user?.roleName?.toLowerCase().includes('account');

  // Filters
  const [businessDate, setBusinessDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [datePreset, setDatePreset] = useState('today');
  const [fromDate, setFromDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [centreId, setCentreId] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [receptionists, setReceptionists] = useState([]);

  // Data states
  const [loading, setLoading] = useState(false);
  const [summaryData, setSummaryData] = useState(null);

  // EOD Settlement inputs
  const [openingCash, setOpeningCash] = useState(0);
  const [actualCash, setActualCash] = useState('');
  const [differenceReason, setDifferenceReason] = useState('');
  const [submittingEod, setSubmittingEod] = useState(false);

  // Reconciliation modal (Accounts/Admin)
  const [reconcileModalOpen, setReconcileModalOpen] = useState(false);
  const [selectedSettlement, setSelectedSettlement] = useState(null);
  const [reconcileStatus, setReconcileStatus] = useState('Reconciled');
  const [reconcileRemarks, setReconcileRemarks] = useState('');
  const [reconciling, setReconciling] = useState(false);

  // Load receptionists list for Accounts/Admin filter
  useEffect(() => {
    if (isAccountsOrAdmin) {
      settlementService.getReceptionists({ centreId: centreId || undefined })
        .then(res => {
          if (res.success && res.data) {
            setReceptionists(res.data);
          }
        })
        .catch(() => {});
    }
  }, [isAccountsOrAdmin, centreId]);

  // Load financial summary
  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        businessDate,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
        centreId: centreId ? parseInt(centreId) : undefined,
        employeeId: employeeId ? parseInt(employeeId) : undefined
      };

      const res = await settlementService.getFinancialSummary(params);
      if (res.success && res.data) {
        setSummaryData(res.data);
        // Pre-fill actual cash if available from settlement
        if (res.data.mySettlement?.actualCashSubmitted !== undefined && res.data.mySettlement?.actualCashSubmitted !== null) {
          setActualCash(res.data.mySettlement.actualCashSubmitted.toString());
          setOpeningCash(res.data.mySettlement.openingCash || 0);
          setDifferenceReason(res.data.mySettlement.differenceReason || '');
        } else {
          // Default actual cash to expected cash for fast workflow
          const exp = (parseFloat(openingCash) || 0) + (res.data.mySettlement?.cashCollection || 0) - (res.data.mySettlement?.cashRefund || 0);
          setActualCash(exp > 0 ? exp.toString() : '0');
        }
      } else {
        showToast(res.message || 'Failed to fetch financial summary', 'error');
      }
    } catch (err) {
      console.error('Error fetching summary:', err);
      showToast('Error loading financial summary', 'error');
    } finally {
      setLoading(false);
    }
  }, [businessDate, fromDate, toDate, centreId, employeeId, openingCash, showToast]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  // Handle Preset Changes
  const handlePresetSelect = (preset) => {
    setDatePreset(preset);
    const today = new Date();
    let f = today.toISOString().split('T')[0];
    let t = today.toISOString().split('T')[0];

    if (preset === 'yesterday') {
      const y = new Date(today);
      y.setDate(today.getDate() - 1);
      f = y.toISOString().split('T')[0];
      t = f;
    } else if (preset === 'thisWeek') {
      const firstDay = new Date(today);
      const day = today.getDay();
      const diff = today.getDate() - day + (day === 0 ? -6 : 1);
      firstDay.setDate(diff);
      f = firstDay.toISOString().split('T')[0];
    } else if (preset === 'thisMonth') {
      f = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
    }

    setFromDate(f);
    setToDate(t);
  };

  // Handle EOD submission
  const handleSubmitEod = async (e) => {
    e.preventDefault();
    const actual = parseFloat(actualCash);
    if (isNaN(actual) || actual < 0) {
      showToast('Please enter a valid cash submitted amount.', 'error');
      return;
    }

    const my = summaryData?.mySettlement;
    const expected = (parseFloat(openingCash) || 0) + (my?.cashCollection || 0) - (my?.cashRefund || 0);
    const diff = expected - actual;

    if (Math.abs(diff) > 0.005 && !differenceReason.trim()) {
      showToast('Reason for Short/Excess Cash is mandatory when difference exists.', 'error');
      return;
    }

    setSubmittingEod(true);
    try {
      const payload = {
        businessDate,
        openingCash: parseFloat(openingCash) || 0,
        actualCashSubmitted: actual,
        differenceReason: differenceReason.trim() || undefined
      };
      const res = await settlementService.submitEodSettlement(payload);
      if (res.success) {
        showToast('Daily Cash Handover & Settlement submitted successfully!', 'success');
        loadSummary();
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to submit settlement.', 'error');
    } finally {
      setSubmittingEod(false);
    }
  };

  // Handle Reconcile
  const handleReconcileSubmit = async () => {
    if (!selectedSettlement?.settlementId) return;
    setReconciling(true);
    try {
      const res = await settlementService.reconcileSettlement({
        settlementId: selectedSettlement.settlementId,
        status: reconcileStatus,
        reconciliationRemarks: reconcileRemarks.trim() || undefined
      });
      if (res.success) {
        showToast('Settlement verified and reconciled successfully!', 'success');
        setReconcileModalOpen(false);
        loadSummary();
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Reconciliation failed.', 'error');
    } finally {
      setReconciling(false);
    }
  };

  const top = summaryData?.topCards || {};
  const my = summaryData?.mySettlement || {};
  const modes = summaryData?.paymentModes || [];
  const consolidated = summaryData?.consolidatedSettlements || [];

  const expectedCashCalc = (parseFloat(openingCash) || 0) + (my.cashCollection || 0) - (my.cashRefund || 0);
  const actualCashNum = parseFloat(actualCash) || 0;
  const currentDiff = expectedCashCalc - actualCashNum;

  return (
    <div className="fin-summary-wrapper">
      {/* =========================================================
          UNIFIED MODERN FILTER TOOLBAR
          ========================================================= */}
      <div className="fin-toolbar-card">
        <div className="fin-toolbar-row">
          {/* Operational Business Date */}
          <div className="fin-control-unit">
            <span className="fin-unit-label">Business Date</span>
            <div className="fin-input-wrapper">
              <span className="fin-input-icon">📅</span>
              <input
                type="date"
                className="fin-input-clean"
                value={businessDate}
                onChange={(e) => {
                  setBusinessDate(e.target.value);
                  setFromDate(e.target.value);
                  setToDate(e.target.value);
                }}
              />
            </div>
          </div>

          {/* Quick Date Presets */}
          <div className="fin-control-unit">
            <span className="fin-unit-label">Quick Range</span>
            <div className="fin-preset-pills">
              {['today', 'yesterday', 'thisWeek', 'thisMonth'].map((p) => (
                <button
                  key={p}
                  type="button"
                  className={`fin-preset-btn ${datePreset === p ? 'active' : ''}`}
                  onClick={() => handlePresetSelect(p)}
                >
                  {p === 'today' ? 'Today' : p === 'yesterday' ? 'Yesterday' : p === 'thisWeek' ? 'This Week' : 'This Month'}
                </button>
              ))}
            </div>
          </div>

          {/* Date Range Inputs */}
          <div className="fin-control-unit">
            <span className="fin-unit-label">Custom Date Range</span>
            <div className="fin-range-pair">
              <input
                type="date"
                className="fin-input-clean"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setDatePreset('custom');
                }}
              />
              <span className="fin-range-arrow">→</span>
              <input
                type="date"
                className="fin-input-clean"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setDatePreset('custom');
                }}
              />
            </div>
          </div>

          {/* Center Selector */}
          <div className="fin-control-unit">
            <span className="fin-unit-label">Center</span>
            <div className="fin-input-wrapper">
              <span className="fin-input-icon">🏥</span>
              <select
                className="fin-select-clean"
                value={centreId}
                onChange={(e) => setCentreId(e.target.value)}
              >
                <option value="">All Diagnostic Centers</option>
                {centres.map((c) => (
                  <option key={c.centreID || c.id} value={c.centreID || c.id}>
                    {c.centreName || c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Receptionist Selector (Accounts/Admin) */}
          {isAccountsOrAdmin && (
            <div className="fin-control-unit">
              <span className="fin-unit-label">Receptionist</span>
              <div className="fin-input-wrapper">
                <span className="fin-input-icon">👤</span>
                <select
                  className="fin-select-clean"
                  value={employeeId}
                  onChange={(e) => setEmployeeId(e.target.value)}
                >
                  <option value="">All Staff (Consolidated)</option>
                  {receptionists.map((r) => (
                    <option key={r.employeeId} value={r.employeeId}>
                      {r.employeeName} ({r.centreName || r.username})
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Action Button */}
          <div className="fin-toolbar-actions">
            <button
              type="button"
              className="fin-btn-apply"
              onClick={loadSummary}
              disabled={loading}
            >
              {loading ? 'Refreshing...' : '⚡ Apply Filter'}
            </button>
          </div>
        </div>
      </div>

      {/* =========================================================
          SECTION 1: TOP SUMMARY CARDS (PERFECT 5x2 SYMMETRICAL GRID)
          ========================================================= */}
      <div className="fin-metrics-section">
        {/* TIER 1: Daily Revenue & Settlement Flow */}
        <div className="fin-tier-header">
          <span className="fin-tier-title">📈 Daily Revenue & Settlement Flow</span>
          <span className="fin-tier-badge">Real-Time Ledger</span>
        </div>
        <div className="fin-grid-5">
          {/* Card 1: Today's Collection */}
          <div className="fin-stat-card theme-blue">
            <div className="fin-stat-top">
              <span className="fin-stat-label">Today's Collection</span>
              <div className="fin-stat-icon-box">💰</div>
            </div>
            <div className="fin-stat-value">
              ₹{Number(top.todayCollection || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
            <div className="fin-stat-footer">Gross Cash & Digital Inflow</div>
          </div>

          {/* Card 2: Today's Refund */}
          <div className="fin-stat-card theme-rose">
            <div className="fin-stat-top">
              <span className="fin-stat-label">Today's Refund</span>
              <div className="fin-stat-icon-box">↩️</div>
            </div>
            <div className="fin-stat-value text-rose">
              ₹{Number(top.todayRefund || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
            <div className="fin-stat-footer">Customer Disbursements</div>
          </div>

          {/* Card 3: Today's Net Collection (Hero) */}
          <div className="fin-stat-card theme-emerald hero">
            <div className="fin-stat-top">
              <span className="fin-stat-label">Today's Net Collection</span>
              <div className="fin-stat-icon-box">📊</div>
            </div>
            <div className="fin-stat-value text-emerald">
              ₹{Number(top.todayNetCollection || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
            <div className="fin-stat-footer">Gross Inflow minus Refunds</div>
          </div>

          {/* Card 4: Pending Settlement */}
          <div className="fin-stat-card theme-amber">
            <div className="fin-stat-top">
              <span className="fin-stat-label">Pending Settlement</span>
              <div className="fin-stat-icon-box">⏳</div>
            </div>
            <div className="fin-stat-value">
              ₹{Number(top.pendingSettlement || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
            <div className="fin-stat-footer">Awaiting Cashier Handover</div>
          </div>

          {/* Card 5: Settlement Difference */}
          <div className={`fin-stat-card ${Math.abs(top.settlementDifference || 0) > 0.005 ? 'theme-rose' : 'theme-slate'}`}>
            <div className="fin-stat-top">
              <span className="fin-stat-label">Settlement Difference</span>
              <div className="fin-stat-icon-box">⚖️</div>
            </div>
            <div className={`fin-stat-value ${Math.abs(top.settlementDifference || 0) > 0.005 ? 'text-rose' : 'text-emerald'}`}>
              ₹{Number(top.settlementDifference || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
            <div className="fin-stat-footer">
              {Math.abs(top.settlementDifference || 0) <= 0.005 ? '✓ Clean Handover (Balanced)' : '⚠️ Discrepancy Found'}
            </div>
          </div>
        </div>

        {/* TIER 2: Payment Channels & Pending Liabilities */}
        <div className="fin-tier-header" style={{ marginTop: '16px' }}>
          <span className="fin-tier-title">💳 Payment Channels & Operational Liabilities</span>
          <span className="fin-tier-badge">Mode Breakdown</span>
        </div>
        <div className="fin-grid-5">
          {/* Card 6: Cash Collection */}
          <div className="fin-stat-card theme-amber">
            <div className="fin-stat-top">
              <span className="fin-stat-label">Cash Collection</span>
              <div className="fin-stat-icon-box">💵</div>
            </div>
            <div className="fin-stat-value">
              ₹{Number(top.cashCollection || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
            <div className="fin-stat-footer">Physical Counter Cash</div>
          </div>

          {/* Card 7: UPI Collection */}
          <div className="fin-stat-card theme-indigo">
            <div className="fin-stat-top">
              <span className="fin-stat-label">UPI / QR Collection</span>
              <div className="fin-stat-icon-box">📱</div>
            </div>
            <div className="fin-stat-value">
              ₹{Number(top.upiCollection || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
            <div className="fin-stat-footer">Instant QR & VPA Transfers</div>
          </div>

          {/* Card 8: Card Collection */}
          <div className="fin-stat-card theme-cyan">
            <div className="fin-stat-top">
              <span className="fin-stat-label">Card Collection</span>
              <div className="fin-stat-icon-box">💳</div>
            </div>
            <div className="fin-stat-value">
              ₹{Number(top.cardCollection || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
            <div className="fin-stat-footer">Credit & Debit POS Swipes</div>
          </div>

          {/* Card 9: Other Payment Modes */}
          <div className="fin-stat-card theme-slate">
            <div className="fin-stat-top">
              <span className="fin-stat-label">Other Payment Modes</span>
              <div className="fin-stat-icon-box">🏦</div>
            </div>
            <div className="fin-stat-value">
              ₹{Number(top.otherCollection || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </div>
            <div className="fin-stat-footer">Bank Transfer, NEFT & Cheque</div>
          </div>

          {/* Card 10: Pending Refunds */}
          <div
            className="fin-stat-card theme-rose interactive"
            onClick={() => onNavigateToTab && onNavigateToTab('refunds')}
            title="Click to navigate to Refunds & Aging workflow"
          >
            <div className="fin-stat-top">
              <span className="fin-stat-label">Pending Refunds</span>
              <div className="fin-stat-icon-box">⚠️</div>
            </div>
            <div className="fin-stat-value text-rose">
              {top.pendingRefundsCount || 0} req
            </div>
            <div className="fin-stat-footer text-link">
              ₹{Number(top.pendingRefundsAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })} • Review Aging →
            </div>
          </div>
        </div>
      </div>

      {/* =========================================================
          SECTION 2: SPLIT LAYOUT (PAYMENT MODES + EOD SETTLEMENT)
          ========================================================= */}
      <div className="fin-split-layout">
        {/* Left Panel: Payment Mode Breakdown & Short Accounting Summary */}
        <div className="fin-modern-panel">
          <div className="fin-panel-top">
            <div className="fin-panel-title-area">
              <span className="fin-panel-icon">📋</span>
              <div>
                <h3>Payment Mode Breakdown & Accounting Summary</h3>
                <p>Configurable payment channels with collection, refund, and net settlements</p>
              </div>
            </div>
            <span className="fin-panel-tag">
              {isAccountsOrAdmin && !employeeId ? 'Consolidated View' : `Assigned: ${my.employeeName || user?.name || 'Staff'}`}
            </span>
          </div>

          <div className="fin-panel-table-wrap">
            <table className="fin-modern-table">
              <thead>
                <tr>
                  <th>Payment Mode</th>
                  <th className="text-right">Collection</th>
                  <th className="text-right">Refund</th>
                  <th className="text-right">Net Activity</th>
                  <th className="text-right">Expected</th>
                  <th className="text-right">Actual</th>
                  <th className="text-right">Difference</th>
                </tr>
              </thead>
              <tbody>
                {modes.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4 text-slate">No payment mode activity recorded for selected range.</td>
                  </tr>
                ) : (
                  modes.map((m, idx) => {
                    const isDiff = Math.abs(m.difference || 0) > 0.005;
                    return (
                      <tr key={idx}>
                        <td>
                          <span className={`fin-channel-badge ${m.paymentMode.toLowerCase().replace(/\s+/g, '')}`}>
                            {m.paymentMode === 'Cash' ? '💵' : m.paymentMode === 'UPI' ? '📱' : m.paymentMode.includes('Card') ? '💳' : '🏦'} {m.paymentMode}
                          </span>
                        </td>
                        <td className="text-right font-medium">
                          ₹{Number(m.collection || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="text-right text-rose font-medium">
                          {m.refund > 0 ? `-₹${Number(m.refund).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '₹0.00'}
                        </td>
                        <td className="text-right text-emerald font-bold">
                          ₹{Number(m.net || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="text-right font-medium text-slate">
                          ₹{Number(m.expectedSettlement || m.net || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="text-right font-medium text-slate">
                          ₹{Number(m.actualSettlement || m.net || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                        <td className={`text-right font-bold ${isDiff ? 'text-rose' : 'text-emerald'}`}>
                          ₹{Number(m.difference || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td>Total Net Activity</td>
                  <td className="text-right">₹{Number(top.todayCollection || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                  <td className="text-right text-rose">
                    {top.todayRefund > 0 ? `-₹${Number(top.todayRefund).toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '₹0.00'}
                  </td>
                  <td className="text-right text-emerald font-bold">₹{Number(top.todayNetCollection || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                  <td className="text-right">₹{Number(top.pendingSettlement || top.todayNetCollection || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                  <td className="text-right">₹{Number(top.pendingSettlement || top.todayNetCollection || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                  <td className="text-right font-bold">₹{Number(top.settlementDifference || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {/* Quick Insights Cards */}
          <div className="fin-insights-strip">
            <div className="fin-insight-chip">
              <span className="chip-label">Gross Collected:</span>
              <span className="chip-val">₹{Number(top.todayCollection || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
            <div className="fin-insight-chip">
              <span className="chip-label">Total Refunded:</span>
              <span className="chip-val text-rose">₹{Number(top.todayRefund || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
            <div className="fin-insight-chip">
              <span className="chip-label">Net Financial Activity:</span>
              <span className="chip-val text-emerald">₹{Number(top.todayNetCollection || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
            <div className="fin-insight-chip">
              <span className="chip-label">Physical Cash Target:</span>
              <span className="chip-val text-blue">₹{Number(expectedCashCalc || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
            </div>
          </div>
        </div>

        {/* Right Panel: End-of-Day Settlement & Cash Handover Register */}
        <div className="fin-modern-panel">
          <div className="fin-panel-top">
            <div className="fin-panel-title-area">
              <span className="fin-panel-icon">🏦</span>
              <div>
                <h3>End-of-Day Settlement & Cash Handover</h3>
                <p>Verify counter float, reconcile collections, and record physical handover</p>
              </div>
            </div>
            <span className={`fin-status-pill ${
              my.status === 'Reconciled' ? 'reconciled' :
              my.status === 'Difference Found' ? 'difference' :
              my.status === 'Under Verification' ? 'verification' :
              my.status === 'Closed' ? 'closed' : 'pending'
            }`}>
              {my.status || 'Pending Handover'}
            </span>
          </div>

          <form onSubmit={handleSubmitEod} className="fin-eod-form">
            <div className="fin-eod-ledger-card">
              {/* Line 1: Opening Counter Cash */}
              <div className="fin-ledger-row">
                <div className="ledger-desc">
                  <strong>Opening Counter Float (Cash)</strong>
                  <span>Initial drawer cash at shift commencement</span>
                </div>
                <div className="ledger-input-group">
                  <span className="currency-prefix">₹</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="fin-ledger-field"
                    value={openingCash}
                    onChange={(e) => setOpeningCash(e.target.value)}
                    placeholder="0.00"
                  />
                </div>
              </div>

              {/* Line 2: Cash Collections */}
              <div className="fin-ledger-row highlight-green">
                <div className="ledger-desc">
                  <span>(+) Today's Cash Collections</span>
                  <small>Recorded patient receipts in cash mode</small>
                </div>
                <div className="ledger-val text-emerald font-bold">
                  + ₹{Number(my.cashCollection || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </div>
              </div>

              {/* Line 3: Cash Refunds */}
              <div className="fin-ledger-row highlight-rose">
                <div className="ledger-desc">
                  <span>(-) Today's Cash Refunds</span>
                  <small>Processed patient cash disbursements</small>
                </div>
                <div className="ledger-val text-rose font-bold">
                  - ₹{Number(my.cashRefund || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </div>
              </div>

              {/* Line 4: Expected Cash Target */}
              <div className="fin-ledger-row callout-blue">
                <div className="ledger-desc">
                  <strong>(=) Expected Cash in Drawer</strong>
                  <small>Opening Float + Cash Collections - Cash Refunds</small>
                </div>
                <div className="ledger-val font-extrabold text-blue" style={{ fontSize: '18px' }}>
                  ₹{Number(expectedCashCalc || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </div>
              </div>

              {/* Line 5: Actual Cash Submitted */}
              <div className="fin-ledger-row highlight-action">
                <div className="ledger-desc">
                  <strong>Actual Cash Submitted</strong>
                  <span>Counted physical cash handed over to Accounts</span>
                </div>
                <div className="ledger-input-group">
                  <span className="currency-prefix">₹</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    required
                    className="fin-ledger-field prominent"
                    value={actualCash}
                    onChange={(e) => setActualCash(e.target.value)}
                    placeholder="Enter physical cash..."
                  />
                  <button
                    type="button"
                    className="fin-auto-btn"
                    onClick={() => setActualCash(expectedCashCalc.toString())}
                    title="Match expected cash automatically"
                  >
                    Match
                  </button>
                </div>
              </div>

              {/* Line 6: Difference Summary */}
              <div className={`fin-ledger-row diff-row ${Math.abs(currentDiff) <= 0.005 ? 'balanced' : currentDiff > 0 ? 'shortage' : 'surplus'}`}>
                <div className="ledger-desc">
                  <strong>Settlement Difference</strong>
                  <small>Expected Cash minus Actual Cash Submitted</small>
                </div>
                <div className="ledger-val font-bold">
                  {Math.abs(currentDiff) <= 0.005 ? (
                    <span className="diff-pill balanced">✓ Perfectly Balanced (₹0.00)</span>
                  ) : currentDiff > 0 ? (
                    <span className="diff-pill shortage">⚠️ Shortage: -₹{Math.abs(currentDiff).toFixed(2)}</span>
                  ) : (
                    <span className="diff-pill surplus">ℹ️ Excess: +₹{Math.abs(currentDiff).toFixed(2)}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Mandatory Reason if Difference Exists */}
            {Math.abs(currentDiff) > 0.005 && (
              <div className="fin-diff-alert-box">
                <label className="fin-diff-label">
                  Mandatory Reason for Short / Excess Cash *
                </label>
                <textarea
                  required
                  rows="2"
                  className="fin-textarea-clean"
                  placeholder="Provide clinical or accounting justification for the cash difference..."
                  value={differenceReason}
                  onChange={(e) => setDifferenceReason(e.target.value)}
                />
              </div>
            )}

            <div className="fin-eod-actions">
              <button
                type="submit"
                className="fin-btn-submit-eod"
                disabled={submittingEod}
              >
                {submittingEod ? 'Submitting Handover...' : '📥 Submit Daily Settlement & Cash Handover'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* =========================================================
          SECTION 3: ACCOUNTS CONSOLIDATED STAFF SETTLEMENTS
          (Visible to Accounts & Admin Users)
          ========================================================= */}
      {isAccountsOrAdmin && (
        <div className="fin-modern-panel" style={{ marginTop: '24px' }}>
          <div className="fin-panel-top">
            <div className="fin-panel-title-area">
              <span className="fin-panel-icon">👥</span>
              <div>
                <h3>Consolidated Receptionist Daily Settlements (Accounts Review)</h3>
                <p>Audit and verify individual receptionist settlements, cash handovers, and discrepancy resolutions</p>
              </div>
            </div>
            <span className="fin-panel-tag">
              {consolidated.length} Cashier Account(s)
            </span>
          </div>

          <div className="fin-panel-table-wrap">
            <table className="fin-modern-table">
              <thead>
                <tr>
                  <th>Settlement # / Date</th>
                  <th>Receptionist</th>
                  <th>Diagnostic Center</th>
                  <th className="text-right">Collection</th>
                  <th className="text-right">Refund</th>
                  <th className="text-right">Net Activity</th>
                  <th className="text-right">Expected Cash</th>
                  <th className="text-right">Actual Handover</th>
                  <th className="text-right">Difference</th>
                  <th>Handover Status</th>
                  <th className="text-center">Accounts Verification</th>
                </tr>
              </thead>
              <tbody>
                {consolidated.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="text-center py-4 text-slate">
                      No cashier settlements found for the selected business date.
                    </td>
                  </tr>
                ) : (
                  consolidated.map((c) => {
                    const hasDiff = Math.abs(c.difference || 0) > 0.005;
                    return (
                      <tr key={c.settlementId}>
                        <td>
                          <div className="font-bold text-slate-dark">{c.settlementNo || `SET-#${c.settlementId}`}</div>
                          <small className="text-slate">{new Date(c.businessDate).toLocaleDateString()}</small>
                        </td>
                        <td>
                          <div className="font-bold">{c.employeeName}</div>
                          <small className="text-slate">Emp #{c.employeeId}</small>
                        </td>
                        <td>
                          <span className="text-slate-dark font-medium">{c.centreName}</span>
                        </td>
                        <td className="text-right font-medium">
                          ₹{Number(c.totalCollection || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="text-right text-rose font-medium">
                          ₹{Number(c.totalRefund || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="text-right text-emerald font-bold">
                          ₹{Number(c.netCollection || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="text-right font-medium text-slate">
                          ₹{Number(c.expectedCash || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="text-right font-bold text-blue">
                          ₹{Number(c.actualCash || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                        <td className={`text-right font-bold ${hasDiff ? 'text-rose' : 'text-emerald'}`}>
                          ₹{Number(c.difference || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                        <td>
                          <span className={`fin-status-pill ${
                            c.status === 'Reconciled' ? 'reconciled' :
                            c.status === 'Difference Found' ? 'difference' :
                            c.status === 'Under Verification' ? 'verification' :
                            c.status === 'Closed' ? 'closed' : 'pending'
                          }`}>
                            {c.status}
                          </span>
                          {c.differenceReason && (
                            <div className="fin-micro-reason" title={c.differenceReason}>
                              Note: {c.differenceReason}
                            </div>
                          )}
                        </td>
                        <td className="text-center">
                          <button
                            type="button"
                            className="fin-btn-audit"
                            onClick={() => {
                              setSelectedSettlement(c);
                              setReconcileStatus(c.status === 'Pending Handover' ? 'Reconciled' : c.status);
                              setReconcileRemarks('');
                              setReconcileModalOpen(true);
                            }}
                          >
                            ⚖️ Reconcile
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* =========================================================
          RECONCILIATION MODAL (ACCOUNTS VERIFICATION)
          ========================================================= */}
      {reconcileModalOpen && selectedSettlement && (
        <div className="fin-modal-overlay">
          <div className="fin-modal-card">
            <div className="fin-modal-top">
              <div className="fin-modal-title">
                <span>⚖️</span>
                <h3>Accounts Settlement Reconciliation</h3>
              </div>
              <button
                type="button"
                className="fin-modal-close"
                onClick={() => setReconcileModalOpen(false)}
              >
                ✕
              </button>
            </div>
            <div className="fin-modal-content-area">
              <div className="fin-audit-summary-box">
                <div className="audit-item">
                  <span className="lbl">Settlement:</span>
                  <span className="val">{selectedSettlement.settlementNo}</span>
                </div>
                <div className="audit-item">
                  <span className="lbl">Receptionist:</span>
                  <span className="val">{selectedSettlement.employeeName} ({selectedSettlement.centreName})</span>
                </div>
                <div className="audit-item">
                  <span className="lbl">Expected Cash:</span>
                  <span className="val text-blue">₹{Number(selectedSettlement.expectedCash || 0).toFixed(2)}</span>
                </div>
                <div className="audit-item">
                  <span className="lbl">Actual Cash:</span>
                  <span className="val text-blue">₹{Number(selectedSettlement.actualCash || 0).toFixed(2)}</span>
                </div>
                <div className="audit-item">
                  <span className="lbl">Difference:</span>
                  <span className={`val ${Math.abs(selectedSettlement.difference || 0) > 0.005 ? 'text-rose' : 'text-emerald'}`}>
                    ₹{Number(selectedSettlement.difference || 0).toFixed(2)}
                  </span>
                </div>
              </div>

              <div className="fin-form-row" style={{ marginTop: '16px' }}>
                <label className="fin-field-label">Reconciliation Status</label>
                <select
                  className="fin-select-clean full"
                  value={reconcileStatus}
                  onChange={(e) => setReconcileStatus(e.target.value)}
                >
                  <option value="Reconciled">🟢 Reconciled (Verified & Approved)</option>
                  <option value="Under Verification">🔵 Under Verification (Audit in Progress)</option>
                  <option value="Difference Found">🔴 Difference Found (Investigation Required)</option>
                  <option value="Closed">⚫ Closed (Finalized)</option>
                </select>
              </div>

              <div className="fin-form-row" style={{ marginTop: '12px' }}>
                <label className="fin-field-label">Audit Remarks / Resolution Notes</label>
                <textarea
                  rows="3"
                  className="fin-textarea-clean"
                  placeholder="Record verification notes, physical cash voucher reference, or discrepancy resolution..."
                  value={reconcileRemarks}
                  onChange={(e) => setReconcileRemarks(e.target.value)}
                />
              </div>
            </div>
            <div className="fin-modal-foot">
              <button
                type="button"
                className="fin-btn-cancel"
                onClick={() => setReconcileModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="fin-btn-confirm"
                disabled={reconciling}
                onClick={handleReconcileSubmit}
              >
                {reconciling ? 'Saving...' : '✓ Confirm & Reconcile'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancialSummaryTab;
